import React, { useState, useEffect, useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";
import qs from "query-string";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CircularProgress from "@material-ui/core/CircularProgress";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import { openApi } from "../../services/api";
import ColorModeContext from "../../layout/themeContext";
import { getBackendUrl } from "../../config";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #1d4ed8 0%, #7c3aed 100%)",
    padding: theme.spacing(2)
  },
  card: {
    maxWidth: 420,
    width: "100%",
    borderRadius: 16,
    boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
    padding: theme.spacing(1)
  },
  logo: {
    display: "block",
    margin: "0 auto 24px",
    maxHeight: 56,
    objectFit: "contain"
  },
  title: {
    fontWeight: 700,
    fontSize: 22,
    textAlign: "center",
    marginBottom: 8,
    color: "#1e293b"
  },
  subtitle: {
    textAlign: "center",
    color: "#64748b",
    fontSize: 14,
    marginBottom: 24
  },
  emailHighlight: {
    fontWeight: 600,
    color: "#1d4ed8"
  },
  codeInput: {
    "& input": {
      textAlign: "center",
      fontSize: 28,
      letterSpacing: 12,
      fontWeight: 700
    }
  },
  submitBtn: {
    marginTop: 16,
    height: 48,
    borderRadius: 10,
    fontWeight: 700,
    fontSize: 16
  },
  resendRow: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: 16,
    gap: 8
  },
  resendText: {
    color: "#64748b",
    fontSize: 13
  },
  resendBtn: {
    color: "#1d4ed8",
    fontSize: 13,
    fontWeight: 600,
    textTransform: "none",
    padding: "0 4px",
    minWidth: 0,
    "&:disabled": {
      color: "#94a3b8"
    }
  },
  timer: {
    color: "#94a3b8",
    fontSize: 13
  }
}));

const VerificarEmail = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  const { email, codigo } = qs.parse(location.search);
  const codigoDaUrl = String(codigo || "").replace(/\D/g, "").slice(0, 6);

  const [code, setCode] = useState(codigoDaUrl);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [countdown, setCountdown] = useState(60);

  // A logo vem da configuracao da empresa, igual ao Login. Antes aqui
  // havia um PNG fixo que era o "SUA LOGO AQUI" do template original.
  const { colorMode } = useContext(ColorModeContext);
  const appName = colorMode?.appName;
  const [siteLogo, setSiteLogo] = useState("");

  useEffect(() => {
    const base = getBackendUrl();
    fetch(`${base}/public-settings/appLogoSite?token=wtV`)
      .then(r => r.json())
      .then(arquivo => { if (arquivo) setSiteLogo(`${base}/public/${arquivo}`); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!email) {
      history.replace("/login");
    }
  }, [email, history]);

  // Veio pelo botao do e-mail: verifica sozinho, sem obrigar a digitar.
  useEffect(() => {
    if (!email || codigoDaUrl.length !== 6) return;
    let cancelado = false;
    (async () => {
      setLoading(true);
      try {
        await openApi.post("/auth/verify-email", { email, code: codigoDaUrl });
        if (cancelado) return;
        toast.success("E-mail confirmado! Agora é só entrar.");
        history.replace("/login");
      } catch (err) {
        if (!cancelado) toastError(err);
      } finally {
        if (!cancelado) setLoading(false);
      }
    })();
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, codigoDaUrl]);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      toast.warning("Digite o código de 6 dígitos");
      return;
    }

    setLoading(true);
    try {
      await openApi.post("/auth/verify-email", { email, code });
      toast.success("E-mail verificado com sucesso! Faça login para acessar.");
      history.replace("/login");
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    try {
      await openApi.post("/auth/resend-verification", { email });
      toast.info("Novo código enviado para " + email);
      setCountdown(60);
    } catch (err) {
      toastError(err);
    } finally {
      setResendLoading(false);
    }
  };

  const handleCodeChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
    setCode(val);
  };

  return (
    <div className={classes.root}>
      <CssBaseline />
      <Container maxWidth="sm" style={{ padding: 0 }}>
        <Card className={classes.card}>
          <CardContent style={{ padding: "32px" }}>
            {siteLogo ? (
              <img src={siteLogo} alt={appName || "Logo"} className={classes.logo} />
            ) : appName ? (
              <Typography className={classes.title} style={{ marginBottom: 24 }}>
                {appName}
              </Typography>
            ) : null}

            <Typography className={classes.title}>
              Verifique seu e-mail
            </Typography>

            <Typography className={classes.subtitle}>
              Enviamos um código de 6 dígitos para{" "}
              <span className={classes.emailHighlight}>{email}</span>.<br />
              Digite-o abaixo para ativar sua conta.
            </Typography>

            <form onSubmit={handleVerify} noValidate>
              <TextField
                variant="outlined"
                fullWidth
                label="Código de verificação"
                value={code}
                onChange={handleCodeChange}
                inputProps={{ maxLength: 6 }}
                className={classes.codeInput}
                placeholder="000000"
                autoFocus
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                className={classes.submitBtn}
                disabled={loading || code.length !== 6}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Verificar e-mail"}
              </Button>
            </form>

            <div className={classes.resendRow}>
              <Typography className={classes.resendText}>
                Não recebeu o código?
              </Typography>
              {countdown > 0 ? (
                <Typography className={classes.timer}>
                  Reenviar em {countdown}s
                </Typography>
              ) : (
                <Button
                  className={classes.resendBtn}
                  onClick={handleResend}
                  disabled={resendLoading}
                  disableRipple
                >
                  {resendLoading ? "Enviando..." : "Reenviar código"}
                </Button>
              )}
            </div>

            <div style={{ textAlign: "center", marginTop: 16 }}>
              <Button
                style={{ color: "#64748b", fontSize: 12, textTransform: "none" }}
                onClick={() => history.push("/login")}
              >
                Voltar para o login
              </Button>
            </div>
          </CardContent>
        </Card>
      </Container>
    </div>
  );
};

export default VerificarEmail;
